import axios from 'axios';
import { AdvisorMessage, AIAnalysis, AiStats, AlertLive, AlertStats, WaveReview, BacktestResult, BacktestStatus, ConsensusReliability, DaytradeBackdrop, DaytradeChart, DaytradeFlowResponse, DaytradeOrderbook, DaytradePaperStats, DaytradeScan, DaytradeSeries, DaytradeNews, ExportProfitabilityResponse, ImpactDetail, LeagueStats, TicketsResponse, SniperResponse, SniperMineResponse, LiveInvestorFlow, AlertExpectations, EntryTiming, RelayWatch, IntradayHistory, MacroResponse, MarketEvent, UpcomingEvents, MarketOutlookSignals, MarketLandscape, MarketStructure, MarketRegime, StockSensitivity, MarketScenarios, MarketForecast, MarketTrackStats, PersonasResponse, PortfolioBriefing, PortfolioStatus, Recommendations, ScannerParams, ScannerResponse, ScannerSignalStats, SearchResult, SectorRadarResponse, SectorResponse, StockDetail, StrategyParams, StrategyResult, ThemeResponse, TradesResponse, WatchlistResponse } from './types';

const client = axios.create({
  // 개발(npm start·3000)이면 8000을 직접 호출. 빌드 서빙·터널(백엔드가 프론트까지 서빙,
  // 같은 origin)이면 상대경로('') → 친구가 터널 URL 하나로 접속해도 API가 같은 주소로 감.
  baseURL: process.env.REACT_APP_API_BASE
    || (window.location.port === '3000' ? 'http://localhost:8000' : ''),
  timeout: 30000,  // 30초 — 캐시된 결과는 빠르게 반환됨
});

// 🔒 관리자 키 자동 동봉(07-11) — 키가 있는 기기만 관리자 API(매매 제안서·자동 비교 등)가 열림. 서버가 검증.
client.interceptors.request.use((cfg) => {
  try {
    const k = localStorage.getItem('scanner_owner_key');
    if (k) cfg.headers['X-Owner-Key'] = k;
  } catch { /* noop */ }
  return cfg;
});

// ── 방문자별 데이터 격리 ───────────────────────────────────────────
// 이 브라우저의 고유 ID를 1회 발급해 localStorage에 보관하고, 모든 요청에 X-User-Id로 실어 보냄.
// 서버는 이 ID로 각자(보유·현금·매매·관심종목) 데이터를 분리 저장한다.
// 호스트 본인(localhost 접속)은 'default'로 고정 → 기존 데이터를 그대로 사용. 친구(IP/터널)는 무작위.
const USER_ID_KEY = 'scanner_user_id';
function getUserId(): string {
  let id = localStorage.getItem(USER_ID_KEY);
  if (!id) {
    const isHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    id = isHost
      ? 'default'
      : 'u_' + (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem(USER_ID_KEY, id);
  }
  return id;
}
client.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  config.headers['X-User-Id'] = getUserId();
  return config;
});

export const fetchMacro = (): Promise<MacroResponse> =>
  client.get<MacroResponse>('/api/macro').then((r) => r.data);

export const fetchSectorRadar = (): Promise<SectorRadarResponse> =>
  client.get<SectorRadarResponse>('/api/sector-radar').then((r) => r.data);

export const fetchExportProfitability = (): Promise<ExportProfitabilityResponse> =>
  client.get<ExportProfitabilityResponse>('/api/export-profitability').then((r) => r.data);

export const fetchScanner = (params: ScannerParams = {}): Promise<ScannerResponse> =>
  client
    .get<ScannerResponse>('/api/scanner', {
      params: {
        sector: params.sector || '전체',
        signal: params.signal || '전체',
        min_score: params.min_score ?? 0,
        limit: params.limit ?? 100,
        strategy: params.strategy || 'minervini',
      },
      timeout: 600000,  // 첫 스캔은 최대 10분 허용
    })
    .then((r) => r.data);

export const fetchThemes = (): Promise<ThemeResponse> =>
  client.get<ThemeResponse>('/api/themes').then((r) => r.data);

export const fetchSectorLeadership = (): Promise<SectorResponse> =>
  client.get<SectorResponse>('/api/sector-leadership', { timeout: 60000 }).then((r) => r.data);

export const fetchScanStatus = (): Promise<{
  scanning: boolean;
  cached_count: number;
  scan_time: string | null;
  stale: boolean;
}> => client.get('/api/scanner/status').then((r) => r.data);

export const triggerRefresh = (force = false): Promise<{ status: string }> =>
  client.post('/api/scanner/refresh', null, { params: { force } }).then((r) => r.data);

export const fetchMarketRegime = (): Promise<MarketRegime> =>
  client.get<MarketRegime>('/api/market-regime').then((r) => r.data);

export const fetchStockDetail = (ticker: string): Promise<StockDetail> =>
  client.get<StockDetail>(`/api/scanner/${ticker}/detail`).then((r) => r.data);

export const fetchStockAI = (ticker: string, force = false): Promise<AIAnalysis> =>
  client.get<AIAnalysis>(`/api/scanner/${ticker}/ai`, { params: force ? { force: true } : {}, timeout: 120000 }).then((r) => r.data);

export const fetchPersonas = (ticker: string): Promise<PersonasResponse> =>
  client.get<PersonasResponse>(`/api/scanner/${ticker}/personas`, { timeout: 120000 }).then((r) => r.data);

export const fetchWatchlist = (): Promise<WatchlistResponse> =>
  client.get<WatchlistResponse>('/api/watchlist', { timeout: 120000 }).then((r) => r.data);

export const fetchDaytradeScan = (mode = 'volume', excludeEtf = false, pin: string[] = []): Promise<DaytradeScan> =>
  client.get<DaytradeScan>('/api/daytrade/scan', { params: { mode, exclude_etf: excludeEtf, pin: pin.join(',') }, timeout: 30000 }).then((r) => r.data);

// 🌐 당일 단타 시장 이벤트(큰손·순환매·국면·장성격·합류) — 서버 단일 소스(기기간 알림 통일)
export const fetchDaytradeMarketEvents = (): Promise<MarketEvent[]> =>
  client.get<{ events: MarketEvent[] }>('/api/daytrade/market-events', { timeout: 10000 }).then((r) => r.data.events || []);

// 📊 알림 기대 분포(과거 실측) · 📈 종목 수급 민감도
export const fetchAlertExpectations = (): Promise<AlertExpectations> =>
  client.get<{ exp: AlertExpectations }>('/api/daytrade/alert-expectations', { timeout: 10000 }).then((r) => r.data.exp || {});
export const fetchStockSensitivity = (ticker: string): Promise<StockSensitivity | null> =>
  client.get<{ sens: StockSensitivity | null }>(`/api/daytrade/stock-sensitivity/${ticker}`, { timeout: 15000 }).then((r) => r.data.sens);
// #6 시간대 등급 — 시간대별 진입 성적(공개)
export const fetchEntryTiming = (): Promise<EntryTiming> =>
  client.get<EntryTiming>('/api/daytrade/entry-timing', { timeout: 10000 }).then((r) => r.data);
// #15 상한가 릴레이 — 지난 거래일 급등 목록('오늘 주목')
export const fetchRelayWatch = (): Promise<RelayWatch> =>
  client.get<RelayWatch>('/api/daytrade/relay-watch', { timeout: 10000 }).then((r) => r.data);
// #16/#18 장중 시세 궤적 — 오늘 이 종목의 [[t,p],...]
export const fetchIntraday = (ticker: string): Promise<IntradayHistory> =>
  client.get<IntradayHistory>(`/api/daytrade/intraday/${ticker}`, { timeout: 10000 }).then((r) => r.data);

export const fetchDaytradeChart = (ticker: string, tf = '1m'): Promise<DaytradeChart> =>
  client.get<DaytradeChart>(`/api/daytrade/chart/${ticker}`, { params: { tf }, timeout: 20000 }).then((r) => r.data);

// 단타 종목별 외국인·기관 추정 수급 — 시세(2초)와 분리해 ~30초로 따로 폴링·병합
export const fetchDaytradeFlow = (tickers: string[]): Promise<DaytradeFlowResponse> =>
  client.get<DaytradeFlowResponse>('/api/daytrade/flow', { params: { tickers: tickers.join(',') }, timeout: 20000 }).then((r) => r.data);

export const fetchDaytradeOrderbook = (ticker: string): Promise<DaytradeOrderbook> =>
  client.get<DaytradeOrderbook>(`/api/daytrade/orderbook/${ticker}`, { timeout: 10000 }).then((r) => r.data);

export const addToWatchlist = (ticker: string): Promise<{ tickers: string[]; message: string }> =>
  client.post('/api/watchlist', { ticker }).then((r) => r.data);

export const removeFromWatchlist = (ticker: string): Promise<{ tickers: string[]; message: string }> =>
  client.delete(`/api/watchlist/${ticker}`).then((r) => r.data);

export const updateHolding = (ticker: string, avg_price: number, quantity: number): Promise<unknown> =>
  client.put(`/api/watchlist/${ticker}/holding`, { avg_price, quantity }).then((r) => r.data);

export const setNote = (ticker: string, note: string): Promise<unknown> =>
  client.put(`/api/watchlist/${ticker}/note`, { note }).then((r) => r.data);

export const sellHolding = (ticker: string, sell_price: number, quantity: number): Promise<unknown> =>
  client.post(`/api/watchlist/${ticker}/sell`, { sell_price, quantity }).then((r) => r.data);

export const buyStock = (ticker: string, buy_price: number, quantity: number): Promise<unknown> =>
  client.post(`/api/watchlist/${ticker}/buy`, { buy_price, quantity }).then((r) => r.data);

export const resetPortfolio = (): Promise<unknown> =>
  client.post('/api/portfolio/reset').then((r) => r.data);

export const fetchTrades = (): Promise<TradesResponse> =>
  client.get<TradesResponse>('/api/trades').then((r) => r.data);

export const fetchPortfolio = (): Promise<PortfolioStatus> =>
  client.get<PortfolioStatus>('/api/portfolio', { timeout: 120000 }).then((r) => r.data);

export const fetchPortfolioBriefing = (): Promise<PortfolioBriefing> =>
  client.get<PortfolioBriefing>('/api/portfolio/briefing', { timeout: 120000 }).then((r) => r.data);

export const updatePortfolioCash = (cash: number): Promise<{ cash: number }> =>
  client.put('/api/portfolio/cash', { cash }).then((r) => r.data);

export const fetchRecommendations = (): Promise<Recommendations> =>
  client.get<Recommendations>('/api/portfolio/recommendations', { timeout: 120000 }).then((r) => r.data);

export const askAdvisor = (question: string, history: AdvisorMessage[]): Promise<{ answer: string }> =>
  client.post('/api/portfolio/ask', { question, history }, { timeout: 120000 }).then((r) => r.data);

export const fetchMarketOutlook = (): Promise<MarketOutlookSignals> =>
  client.get<MarketOutlookSignals>('/api/market-outlook', { timeout: 120000 }).then((r) => r.data);

export const fetchMarketLandscape = (): Promise<MarketLandscape> =>
  client.get<MarketLandscape>('/api/market/landscape', { timeout: 20000 }).then((r) => r.data);

// 🏦 구조 계기판(줍줍 3단계) — 하이닉스 신용·공매도 추이 + 내일 이벤트 브리핑
export const fetchMarketStructure = (): Promise<MarketStructure> =>
  client.get<MarketStructure>('/api/market/structure', { timeout: 20000 }).then((r) => r.data);

// 수급 전용 경량 — 장중 ~60초 폴링용
export const fetchInvestorFlow = (): Promise<LiveInvestorFlow> =>
  client.get<LiveInvestorFlow>('/api/market/investor-flow', { timeout: 15000 }).then((r) => r.data);

// 단타 시장 배경(우호/중립/역풍 + 보정) — AI 0원, 계산값. ~60초 폴링.
export const fetchDaytradeBackdrop = (): Promise<DaytradeBackdrop> =>
  client.get<DaytradeBackdrop>('/api/daytrade/backdrop', { timeout: 20000 }).then((r) => r.data);

// 📈 스파크라인 궤적(오늘 큰손·프로그램·베이시스·하이닉스·코스피) — 60초 폴, 서버 45초 캐시
export const fetchDaytradeSeries = (): Promise<DaytradeSeries> =>
  client.get<DaytradeSeries>('/api/daytrade/series', { timeout: 15000 }).then((r) => r.data);

// 🗞️ 상황판 2층 — '지금 뉴스'(넓은 시황·반도체) + 오늘의 시간표(결정론 달력) — 5분 폴, 서버 8분 캐시
export const fetchDaytradeNews = (): Promise<DaytradeNews> =>
  client.get<DaytradeNews>('/api/daytrade/news', { timeout: 15000 }).then((r) => r.data);

// 🎫 뉴 단타 — 매매 제안서 엔진(제안·자동 채점)
export const fetchTickets = (): Promise<TicketsResponse> =>
  client.get<TicketsResponse>('/api/daytrade/tickets', { timeout: 15000 }).then((r) => r.data);

// 🎯 승부처 옛 저격 기능 기록 장부(07-15 전면 개편, 관리자 전용)
export const fetchSniper = (): Promise<SniperResponse> =>
  client.get<SniperResponse>('/api/daytrade/sniper', { timeout: 15000 }).then((r) => r.data);

// 🔎 승부처 조합 발굴 후보(2단계, 관리자 전용)
export const fetchSniperMine = (): Promise<SniperMineResponse> =>
  client.get<SniperMineResponse>('/api/daytrade/sniper-mine', { timeout: 15000 }).then((r) => r.data);

// 🏟️ 자동 비교 연대기(📊 통계 탭, 관리자 전용)
export const fetchLeague = (): Promise<LeagueStats> =>
  client.get<LeagueStats>('/api/daytrade/league', { timeout: 15000 }).then((r) => r.data);

// 📊 알림 성적 라이브(📊 통계 탭 검증 중, 관리자 전용) — 오늘 kind별 울림·5분 채점 + 하이닉스 힘 감지↔가격경보 타임라인 + 검증 중/기계 건강
// 📅 date 없으면 오늘(라이브), scope='all'이면 전 기간 합산 — 통계탭 ② '오늘' 섹션의 날짜 이동용.
export const fetchAlertLive = (opts?: { date?: string; scope?: 'day' | 'all' }): Promise<AlertLive> =>
  client.get<AlertLive>('/api/owner/alert-live', {
    params: { ...(opts?.date ? { date: opts.date } : {}), ...(opts?.scope === 'all' ? { scope: 'all' } : {}) },
    timeout: 15000,
  }).then((r) => r.data);

// 🌊 파도 복기(관리자 전용) — day 미지정=최근 저장분. 없는 날은 404 → null로 흡수(오늘은 rebuild=1로 즉석 생성).
export const fetchWaveReview = (day?: string, rebuild = false): Promise<WaveReview | null> =>
  client.get<WaveReview>('/api/owner/wave-review', { params: { ...(day ? { day } : {}), ...(rebuild ? { rebuild: true } : {}) }, timeout: 20000 })
    .then((r) => r.data).catch(() => null);

// 🧮 수급→지수 계산기 상세(#88) — 옛 조종 화면 수급영향 줄 클릭 시 1회 조회
export const fetchImpactDetail = (): Promise<ImpactDetail> =>
  client.get<ImpactDetail>('/api/daytrade/impact-detail', { timeout: 20000 }).then((r) => r.data);

// 단타 지금 살 만함 모의매매 성적표(포워드 테스트)
export const fetchDaytradePaperStats = (): Promise<DaytradePaperStats> =>
  client.get<DaytradePaperStats>('/api/daytrade/paper/stats', { timeout: 25000 }).then((r) => r.data);

// 메인 스캐너 신호 성적표(포워드 테스트)
export const fetchScannerSignalStats = (): Promise<ScannerSignalStats> =>
  client.get<ScannerSignalStats>('/api/scanner/signal-stats', { timeout: 25000 }).then((r) => r.data);

// 단타 알림 적중 검증
export const fetchAlertStats = (): Promise<AlertStats> =>
  client.get<AlertStats>('/api/daytrade/alert-stats', { timeout: 20000 }).then((r) => r.data);
export const postAlertLog = (body: unknown): Promise<{ ok: boolean }> =>
  client.post('/api/daytrade/alert-log', body, { timeout: 8000 }).then((r) => r.data);

// 웹푸시 — VAPID 공개키 조회 + 브라우저 구독 등록(잠금화면 알림)
export const fetchPushKey = (): Promise<{ key: string }> =>
  client.get<{ key: string }>('/api/push/key', { timeout: 10000 }).then((r) => r.data);
export const postPushSubscribe = (sub: unknown): Promise<{ ok: boolean; subscriptions: number }> =>
  client.post('/api/push/subscribe', sub, { timeout: 10000 }).then((r) => r.data);
export const postPushUnsubscribe = (endpoint: string): Promise<{ ok: boolean; subscriptions: number }> =>
  client.post('/api/push/unsubscribe', { endpoint }, { timeout: 10000 }).then((r) => r.data);
export const postPushPins = (body: unknown): Promise<{ ok: boolean; pins: number }> =>
  client.post('/api/push/pins', body, { timeout: 10000 }).then((r) => r.data);
export const postPushWatch = (body: unknown): Promise<{ ok: boolean; watch: number }> =>
  client.post('/api/push/watch', body, { timeout: 10000 }).then((r) => r.data);
export const postPushPref = (body: unknown): Promise<{ ok: boolean }> =>
  client.post('/api/push/pref', body, { timeout: 10000 }).then((r) => r.data);

// 임박·회자 이벤트 레이더(연기금 리밸런싱·MSCI·만기 등) — 뉴스 키워드 감지
export const fetchMarketEvents = (): Promise<UpcomingEvents> =>
  client.get<UpcomingEvents>('/api/market-events', { timeout: 20000 }).then((r) => r.data);

// 시장분석 국면 성적표(시나리오 예측 vs 실제 코스피) — 관리자 전용 뷰
export const fetchMarketTrackStats = (): Promise<MarketTrackStats> =>
  client.get<MarketTrackStats>('/api/market-outlook/track-stats', { timeout: 20000 }).then((r) => r.data);

// 종목 AI 심층분석 성적표(행동결론 적중) — 관리자 전용 뷰
export const fetchAiStats = (): Promise<AiStats> =>
  client.get<AiStats>('/api/scanner/ai-stats', { timeout: 20000 }).then((r) => r.data);

export const fetchMarketScenarios = (force = false): Promise<MarketScenarios> =>
  client.get<MarketScenarios>(`/api/market-outlook/scenarios${force ? '?force=true' : ''}`, { timeout: 120000 }).then((r) => r.data);
export const fetchMarketForecast = (force = false): Promise<MarketForecast> =>  client.get<MarketForecast>(`/api/market/forecast${force ? '?force=true' : ''}`, { timeout: 120000 }).then((r) => r.data);

// 📌 pins(핀 티커 목록)도 함께 보낸다(08-12) — 핀은 원래 push 구독이 있어야만 서버로 갔는데(syncPins),
//   폰 푸시를 안 쓰는 사용자는 서버에 핀이 영영 없었다. 📄 공시 경고가 '핀 종목'을 대상에 넣으려면 필요하다.
export const postWatchSync = (tickers: string[], names: Record<string, string>, pins?: string[]): Promise<void> =>
  client.post('/api/daytrade/watch-sync', { tickers, names, ...(pins ? { pins } : {}) }, { timeout: 10000 }).then(() => undefined);

export const fetchMarketBrief3 = (): Promise<{ lines: string[]; sources: string[]; made_at?: string; _fallback?: boolean }> =>
  client.get('/api/market/brief3', { timeout: 90000 }).then((r) => r.data as { lines: string[]; sources: string[] });

export const fetchDaytradeAutopsy = (): Promise<Record<string, unknown>> =>
  client.get('/api/daytrade/autopsy', { timeout: 60000 }).then((r) => r.data as Record<string, unknown>);

export const fetchDaytradeAutopilot = (): Promise<Record<string, unknown>> =>
  client.get('/api/daytrade/autopilot', { timeout: 30000 }).then((r) => r.data as Record<string, unknown>);

export const fetchConsensusReliability = (): Promise<ConsensusReliability> =>
  client.get<ConsensusReliability>('/api/consensus/reliability', { timeout: 60000 }).then((r) => r.data);

export const searchStocks = (q: string): Promise<SearchResult[]> =>
  client.get<SearchResult[]>('/api/search', { params: { q } }).then((r) => r.data);

export const runBacktest = (): Promise<{ status: string }> =>
  client.post('/api/backtest/run').then((r) => r.data);

export const fetchBacktestStatus = (): Promise<BacktestStatus> =>
  client.get<BacktestStatus>('/api/backtest/status').then((r) => r.data);

export const fetchBacktestResult = (): Promise<BacktestResult> =>
  client.get<BacktestResult>('/api/backtest/result').then((r) => r.data);

export const runStrategyBacktest = (params?: Partial<StrategyParams>): Promise<{ status: string }> =>
  client.post('/api/backtest/strategy/run', params ?? {}).then((r) => r.data);

export const fetchStrategyStatus = (): Promise<BacktestStatus> =>
  client.get<BacktestStatus>('/api/backtest/strategy/status').then((r) => r.data);

export const fetchStrategyResult = (): Promise<StrategyResult> =>
  client.get<StrategyResult>('/api/backtest/strategy/result').then((r) => r.data);
